import React, { useEffect, useMemo, useState } from "react";
import { FiPlus, FiSearch, FiX } from "react-icons/fi";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { useHistory, useLocation } from "react-router-dom";
import { getAuth } from "../../services/localStorage";
import { API_ENDPOINT } from "../../services/api";
import ConfirmDialog from "../ConfirmDialog";
import Labels from "../../services/apiLabels";
import OfficeRowMenu from "./OfficeRowMenu";
import OfficeAppointmentDetail from "../OfficeAppointments/OfficeAppointmentDetail";
import {
  formatNextAppointment,
  nextAppointment,
} from "../OfficeAppointments/appointmentHelpers";
import {
  authHeaders,
  isApiSuccess,
  listFromPayload,
  matchesSearch,
  unwrapOffice,
  officeId,
  pick,
  readJson,
} from "./officeHelpers";

const PAGE_SIZE_OPTIONS = [5, 10, 20];
const LIST_PATH = "/dealers/dashbord/offices";

function OfficeList() {
  const history = useHistory();
  const location = useLocation();
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState({
    type: location.state?.notice ? "success" : "",
    text: location.state?.notice || "",
  });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewingAppointment, setViewingAppointment] = useState(null);

  const auth = getAuth();

  const loadOffices = async () => {
    if (!auth?.token) {
      setLoading(false);
      setOffices([]);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINT(Labels.office_listing), {
        headers: authHeaders(auth.token),
      });
      const json = await readJson(response);
      if (!isApiSuccess(response, json)) throw new Error("list failed");
      setOffices(listFromPayload(json).map((office) => unwrapOffice(office)));
    } catch {
      setOffices([]);
      setBanner((prev) =>
        prev.type === "success"
          ? prev
          : {
              type: "error",
              text: "Could not load offices. Please try again.",
            }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOffices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!location.state?.notice) return undefined;
    history.replace({ pathname: LIST_PATH, state: {} });
    return undefined;
  }, [history, location.state]);

  useEffect(() => {
    setPage(1);
  }, [search, pageSize]);

  const filtered = useMemo(
    () => offices.filter((office) => matchesSearch(office, search)),
    [offices, search]
  );

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize) || 1);
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, filtered.length);
  const pageRows = filtered.slice(pageStart, pageEnd);
  const isLoading = Boolean(loading) && offices.length === 0;

  const confirmDelete = async () => {
    const id = officeId(deleteTarget);
    if (!id || !auth?.token) return;
    setDeleting(true);
    try {
      const response = await fetch(
        API_ENDPOINT(`${Labels.delete_office}${id}/`),
        {
          method: "DELETE",
          headers: authHeaders(auth.token),
        }
      );
      if (!response.ok && response.status !== 204) {
        throw new Error("delete failed");
      }
      setDeleteTarget(null);
      setBanner({ type: "success", text: "Office removed." });
      await loadOffices();
    } catch {
      setBanner({
        type: "error",
        text: "Could not delete this office. Please try again.",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="dd-section dd-offices">
      {banner.text ? (
        <p
          className={
            banner.type === "success"
              ? "dd-password__hint"
              : "dd-password__error"
          }
          role="status"
        >
          {banner.text}
        </p>
      ) : null}

      <div className="dd-toolbar">
        <label className="dd-toolbar__search">
          <FiSearch size={16} aria-hidden />
          <input
            type="search"
            placeholder="Search address, area, or postcode"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search ? (
            <button
              type="button"
              className="dd-toolbar__clear-input"
              onClick={() => setSearch("")}
              aria-label="Clear search"
            >
              <FiX size={14} />
            </button>
          ) : null}
        </label>

        <label className="dd-toolbar__field">
          <span>Per page</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>

        <label className="dd-toolbar__field">
          <span>List</span>
          <button
            type="button"
            className="dd-toolbar__action-btn"
            onClick={loadOffices}
            disabled={loading}
          >
            Refresh
          </button>
        </label>

        <label className="dd-toolbar__field">
          <span>Office</span>
          <button
            type="button"
            className="dd-btn dd-btn--primary"
            onClick={() => history.push(`${LIST_PATH}/new`)}
          >
            <FiPlus size={16} aria-hidden />
            Add office
          </button>
        </label>
      </div>

      <div className="dd-toolbar__meta">
        {filtered.length ? (
          <span>
            Showing {pageStart + 1}–{pageEnd} of {filtered.length}
            {filtered.length !== offices.length
              ? ` (filtered from ${offices.length})`
              : ""}
          </span>
        ) : (
          <span>No offices to show</span>
        )}
      </div>

      {isLoading ? (
        <div className="dd-empty">
          <strong>Loading offices…</strong>
        </div>
      ) : !offices.length ? (
        <div className="dd-empty">
          <strong>No offices yet</strong>
          <p>Add an inspection centre to use it when booking appointments.</p>
          <button
            type="button"
            className="dd-btn dd-btn--primary"
            style={{ marginTop: "0.75rem" }}
            onClick={() => history.push(`${LIST_PATH}/new`)}
          >
            <FiPlus size={16} aria-hidden />
            Add office
          </button>
        </div>
      ) : !filtered.length ? (
        <div className="dd-empty">
          <strong>No matches</strong>
          <p>Try a different search.</p>
        </div>
      ) : (
        <>
          <div className="dd-table-wrap">
            <table className="dd-table">
              <thead>
                <tr>
                  <th scope="col">Address</th>
                  <th scope="col">Area</th>
                  <th scope="col">Postcode</th>
                  <th scope="col">Next appointment</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((office, index) => {
                  const id = officeId(office);
                  const address = pick(office, ["Address", "address"], "—");
                  const next = nextAppointment(office);
                  const nextLabel = formatNextAppointment(office);
                  const go = (path) =>
                    history.push({
                      pathname: `${LIST_PATH}/${id}${path}`,
                      state: { office },
                    });
                  return (
                    <tr key={id || `office-${index}`}>
                      <td>
                        {id ? (
                          <button
                            type="button"
                            className="dd-offices__address"
                            onClick={() => go("/edit")}
                          >
                            {address}
                          </button>
                        ) : (
                          address
                        )}
                      </td>
                      <td>{pick(office, ["Area", "area"], "—")}</td>
                      <td>{pick(office, ["Postcode", "postcode"], "—")}</td>
                      <td>
                        {next && nextLabel !== "—" ? (
                          <button
                            type="button"
                            className="dd-offices__address"
                            onClick={() => setViewingAppointment(next)}
                          >
                            {nextLabel}
                          </button>
                        ) : (
                          nextLabel
                        )}
                      </td>
                      <td>
                        <OfficeRowMenu
                          disabled={!id}
                          onAppointments={() => go("/appointments")}
                          onHours={() => go("/hours")}
                          onUsers={() => go("/users")}
                          onEdit={() => go("/edit")}
                          onDelete={() => setDeleteTarget(office)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filtered.length > pageSize ? (
            <div className="dd-pagination">
              <PaginationControl
                page={safePage}
                between={3}
                total={filtered.length}
                limit={pageSize}
                changePage={setPage}
                ellipsis={1}
              />
            </div>
          ) : null}
        </>
      )}

      <OfficeAppointmentDetail
        item={viewingAppointment}
        onClose={() => setViewingAppointment(null)}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete this office?"
        message={
          deleteTarget
            ? `Remove ${pick(
                deleteTarget,
                ["Address", "address"],
                "this office"
              )}? This cannot be undone.`
            : ""
        }
        confirmLabel="Delete office"
        confirming={deleting}
        onCancel={() => (!deleting ? setDeleteTarget(null) : null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default OfficeList;
